import React from 'react';
import { coverChainFor, coverImageFor, type CoverInput, type CoverSource } from '@/lib/coverImage';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * COVER IMAGE — the ONLY <img> used to render a project cover.
 * ───────────────────────────────────────────────────────────────────────────
 * THE BLANK-CARD REGRESSION LIVED HERE. Read this before touching the file.
 *
 * The previous version hid the <img> behind `opacity-0` until React's onLoad
 * fired. That is unsafe: a browser only fires `load` for a request it actually
 * starts. When the file is ALREADY in the memory/disk cache the element can be
 * `complete` before React attaches the handler, so the event never arrives and
 * the card stays a pulsing gray box forever — with the photo downloaded and
 * sitting right there.
 *
 * That is precisely why the bug looked random and "came back":
 *   • a grid of 12 painting leads shares ONE trade cover URL — the first card
 *     downloads it and paints, cards 2-12 mount against the cache and go blank;
 *   • every back-navigation (Jobs → detail → back) re-mounts against the cache;
 *   • bfcache / tab restore does the same.
 * Nothing about the URLs was wrong, which is why re-uploading images or
 * re-checking the resolver never fixed it for long.
 *
 * This version:
 *   1. reads `img.complete && img.naturalWidth > 0` from a ref on mount and on
 *      every src change, so a cached hit is revealed synchronously;
 *   2. only resets the loaded flag when the src REALLY changes (the old mount
 *      effect could un-reveal an image that had already painted);
 *   3. walks the ENTIRE candidate chain on error — every uploaded photo, then
 *      the trade/category cover, then the branded cover (coverChainFor);
 *   4. runs a watchdog that reveals the image anyway if no event lands in 8s,
 *      so a slow decode can never hold a blank box;
 *   5. if literally every candidate fails, paints a VISIBLE branded tile
 *      instead of an invisible <img>. There is no code path left that renders
 *      an empty container.
 */

interface Props {
  listing: CoverInput | null | undefined;
  /** Accessible name. Pass '' for a decorative category/fallback cover. */
  alt: string;
  className?: string;
  /** Tailwind aspect ratio utility for the wrapper. */
  aspect?: string;
  /** Above-the-fold covers should pass false to skip lazy loading. */
  lazy?: boolean;
  /** Notifies the parent which tier actually rendered (used by the audit panel). */
  onResolved?: (source: CoverSource) => void;
  children?: React.ReactNode;
}

const CoverImage: React.FC<Props> = ({
  listing,
  alt,
  className = '',
  aspect = 'aspect-[4/3]',
  lazy = true,
  onResolved,
  children,
}) => {
  // Full ordered candidate list: uploads → trade cover → branded cover.
  const chain = React.useMemo(() => coverChainFor(listing), [
    JSON.stringify((listing as Record<string, unknown>)?.photos ?? null),
    listing?.trade,
    listing?.trade_slug,
    listing?.category,
    listing?.category_slug,
    listing?.project_type,
  ]);

  const [step, setStep] = React.useState(0);
  const [loaded, setLoaded] = React.useState(false);
  /** True only when EVERY candidate failed to decode. */
  const [exhausted, setExhausted] = React.useState(false);

  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const shownSrc = React.useRef<string>('');

  const current = chain[Math.min(step, chain.length - 1)] || coverImageFor(listing);
  const src = current.src;
  const source = current.source;

  // Restart the chain when the row (and therefore the candidate list) changes.
  React.useEffect(() => {
    setStep(0);
    setExhausted(false);
  }, [chain]);

  /**
   * CACHED-IMAGE GUARD — the actual fix.
   * If the element already holds decoded pixels, reveal it now; the load event
   * for that request happened before React could subscribe to it.
   */
  React.useEffect(() => {
    if (shownSrc.current !== src) {
      shownSrc.current = src;
      setLoaded(false);
    }
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) setLoaded(true);
  }, [src]);

  /**
   * WATCHDOG — never let a missing event leave a blank rectangle on screen.
   * If neither load nor error has landed after 8s, reveal whatever the element
   * has. `img` with a live src always paints something once revealed, and the
   * shimmer stops pretending the card is still working.
   */
  React.useEffect(() => {
    if (loaded || exhausted) return;
    const id = window.setTimeout(() => setLoaded(true), 8000);
    return () => window.clearTimeout(id);
  }, [src, loaded, exhausted]);

  React.useEffect(() => {
    if (loaded) onResolved?.(source);
    // onResolved is intentionally not a dependency: consumers pass inline
    // arrows, which would re-fire this on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, source]);

  /**
   * Runtime degradation: next upload → trade cover → branded cover.
   * `exhausted` is set ONLY when the candidate that just failed was the last
   * one in the chain, so a failing upload can never skip the category cover.
   */
  const stepRef = React.useRef(0);
  stepRef.current = step;

  const handleError = React.useCallback(() => {
    const last = chain.length - 1;
    if (stepRef.current >= last) {
      setExhausted(true);
      return;
    }
    setStep((s) => Math.min(s + 1, last));
  }, [chain.length]);



  return (
    <div className={`relative w-full overflow-hidden bg-slate-200 ${aspect} ${className}`}>
      {/* Tinted shimmer beneath the photo — covers the lazy-load window only. */}
      {!loaded && !exhausted && (
        <span
          aria-hidden="true"
          className="absolute inset-0 animate-pulse bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200"
        />
      )}

      {/*
        LAST-RESORT TILE. Reached only if every uploaded photo AND both library
        covers fail to decode (total CDN outage, offline, blocked host). It is a
        real, visible branded surface — the container is never empty.
      */}
      {exhausted && (
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900"
        >
          <svg viewBox="0 0 24 24" className="h-10 w-10 text-orange-400/90" fill="none" aria-hidden="true">
            <path
              d="M3 19V9.5L12 4l9 5.5V19a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1Z"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}

      {!exhausted && (
        <img
          ref={imgRef}
          key={src}
          src={src}
          alt={alt}
          aria-hidden={alt ? undefined : true}
          loading={lazy ? 'lazy' : 'eager'}
          decoding="async"
          fetchPriority={lazy ? 'auto' : 'high'}
          data-cover-source={source}
          onLoad={() => setLoaded(true)}
          onError={handleError}
          className={`relative h-full w-full object-cover object-center transition-opacity duration-300 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}

      {children}
    </div>
  );
};

export default CoverImage;
